import React, { useMemo, useRef } from 'react';
import { Dropdown } from 'bootstrap';
import { Link } from 'react-router-dom';
import { BoxArrowRight, List } from 'react-bootstrap-icons';
import userImg from '../assets/images/userimg.png';
import logo from '../assets/images/logo.png';
import { API_BASE_URL } from '../utils/api';

const resolveProfilePhoto = (photo) => {
    if (!photo) {
        return userImg;
    }

    if (photo.startsWith('http://') || photo.startsWith('https://') || photo.startsWith('data:')) {
        return photo;
    }

    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const normalizedPath = photo.startsWith('/') ? photo : `/${photo}`;

    return `${baseUrl}${normalizedPath}`;
};

const Header = ({ title, admin, onToggleSidebar, isSidebarOpen, activeCompany, companies, onSelectCompany }) => {
    const dropdownButtonRef = useRef(null);
    const dropdownInstanceRef = useRef(null);
    const companyDropdownButtonRef = useRef(null);
    const companyDropdownInstanceRef = useRef(null);

    const toggleDropdown = () => {
        if (!dropdownInstanceRef.current && dropdownButtonRef.current) {
            dropdownInstanceRef.current = new Dropdown(dropdownButtonRef.current);
        }
        dropdownInstanceRef.current.toggle();
    };

    const toggleCompanyDropdown = () => {
        if (!companyDropdownInstanceRef.current && companyDropdownButtonRef.current) {
            companyDropdownInstanceRef.current = new Dropdown(companyDropdownButtonRef.current);
        }
        companyDropdownInstanceRef.current.toggle();
    };

    const { displayName, displayRole } = useMemo(() => {
        if (!admin) {
            return {
                displayName: 'Admin User',
                displayRole: 'Administrator',
            };
        }

        const resolvedName = admin.name?.trim() || admin.username || 'Admin User';
        const resolvedRole = admin.role?.name || admin.role?.description || 'Administrator';

        return {
            displayName: resolvedName,
            displayRole: resolvedRole,
        };
    }, [admin]);

    const profilePhoto = useMemo(() => resolveProfilePhoto(admin?.photo), [admin?.photo]);
    const profilePath = admin?.id ? `/settings/users/${admin.id}` : '/settings/users';
    const companyName = activeCompany?.name || 'Select Company';
    const hasCompanyOptions = Array.isArray(companies) && companies.length > 0;

    const handleSelectCompany = (company) => {
        if (onSelectCompany) {
            onSelectCompany(company);
        }
    };

    const handleToggleSidebar = () => {
        if (onToggleSidebar) {
            onToggleSidebar();
        }
    };

    return (
        <nav className="navbar navbar-light bg-white shadow-sm py-2 sticky-top">
            <div className="container-fluid d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-outline-danger d-inline-flex d-md-none align-items-center justify-content-center"
                        onClick={handleToggleSidebar}
                        aria-label="Toggle navigation"
                        aria-expanded={Boolean(isSidebarOpen)}
                    >
                        <List size={20} />
                    </button>
                    {title ? (
                        <h5 className="mb-0">{title}</h5>
                    ) : (
                        <img src={logo} alt="Grace Super Market" style={{ height: '35px' }} />
                    )}
                    <div className="dropdown">
                        <button
                            ref={companyDropdownButtonRef}
                            className="btn btn-outline-secondary dropdown-toggle"
                            type="button"
                            onClick={toggleCompanyDropdown}
                            disabled={!hasCompanyOptions}
                        >
                            {companyName}
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            {hasCompanyOptions ? (
                                companies.map((company) => (
                                    <li key={company.id}>
                                        <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => handleSelectCompany(company)}
                                        >
                                            {company.name || company.company_name || 'Company'}
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <li>
                                    <span className="dropdown-item-text text-muted">No companies</span>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Link
                        to="/logout"
                        className="btn btn-outline-danger d-inline-flex d-md-none align-items-center justify-content-center"
                        aria-label="Logout"
                    >
                        <BoxArrowRight size={20} />
                    </Link>
                    <div className="dropdown position-relative">
                        <button
                            ref={dropdownButtonRef}
                            className="btn dropdown-toggle border-0 d-flex align-items-center"
                            type="button"
                            onClick={toggleDropdown}
                        >
                            <img
                                src={profilePhoto}
                                alt="User"
                                className="rounded-circle me-2"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = userImg;
                                }}
                            />
                            <div className="d-flex flex-column align-items-start me-2">
                                <span className="fw-semibold">{displayName}</span>
                                <small className="text-muted">{displayRole}</small>
                            </div>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                            <li>
                                <Link to={profilePath} className="dropdown-item">
                                    Edit Profile
                                </Link>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                                <Link to="/logout" className="dropdown-item">
                                    Logout
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Header;
